// ייבוא וסנכרון (03 §1–2).
import { z } from 'zod';
import { Id, Json, Ts, rowBase } from './common.ts';

/** סוגי טרנספורמציה גנריים — הפלטפורמה מכירה; התבנית (04) אומרת על אילו עמודות. */
export const TransformKind = z.enum(['minutes_since_1988', 'visual_hebrew', 'visual_latin', 'scale_1000', 'percent_div_100', 'hebrew_full_reverse', 'html_text']);
export type TransformKind = z.infer<typeof TransformKind>;

/**
 * טיפוסי Postgres המותרים ב-etl (01 §2). `date` = תאריך לוח בלי שעה (עמודת תאריך-בלבד במקור, למשל תאריך חשבונית):
 * נשמר כ-date בשעון הקיר של המקור, לא כרגע UTC — כדי ש"1 בינואר" יישאר 1 בינואר בכל session (נמדד בגלאור: 461 חשבוניות
 * של 1.1 נפלו ל-31.12 ב-UTC). זמן עם שעה נשאר timestamptz.
 */
export const EtlType = z.enum(['text', 'bigint', 'double precision', 'numeric', 'timestamptz', 'date', 'boolean']);
export type EtlType = z.infer<typeof EtlType>;

/** שם עמודה/ישות לוגי ב-etl (lower-case). */
export const EtlName = z.string().regex(/^[a-z_][a-z0-9_]{0,62}$/, 'etl identifier must be lower snake_case');

export const ImportColumn = z.object({
  name: EtlName,
  source: z.string().min(1),
  sourceType: z.string(),
  etlType: EtlType,
  transforms: z.array(TransformKind),
  ignore: z.boolean().optional(),
});
export type ImportColumn = z.infer<typeof ImportColumn>;

export const ImportFilter = z.object({
  static: z.string().optional(),
  in: z.object({ column: z.string(), keysSql: z.string() }).optional(),
});
export type ImportFilter = z.infer<typeof ImportFilter>;

/** האות החם של טבלה (03 §2.1): מה מגלה שינוי בין סבב לסבב. `none` = אין חם — הסבב הוא הכול. */
export const SignalKind = z.enum(['engine_log', 'engine_marker', 'update_column', 'document_date', 'none']);
export type SignalKind = z.infer<typeof SignalKind>;

export const HotSpec = z.object({
  signal: SignalKind,
  /** update_column / document_date: key של שדה מיובא (אותיות קטנות). engine_log/engine_marker/none: אין. */
  column: z.string().optional(),
  /** update_column: 7 · document_date: 60 — כמה ימים אחורה מרעננים בכל מחזור. */
  days: z.number().int().positive().optional(),
});
export type HotSpec = z.infer<typeof HotSpec>;

export const ColdSpec = z.object({
  /** window: דליים לפי עמודת תאריך · key: דליים לפי מפתח מספרי · all: כל הטבלה (≤ 20,000 שורות) בכל מחזור */
  by: z.enum(['window', 'key', 'all']),
  /** window: ימים (30) · key: מפתחות לדלי (5,000) · all: לא רלוונטי (1) */
  bucket: z.number().int().positive(),
  /** תוך כמה ימים עובר הסבב על כל האופק — 1 בלי לוג מנוע, 7 עם engine_log */
  rotationDays: z.number().int().positive(),
});
export type ColdSpec = z.infer<typeof ColdSpec>;

/**
 * מפרט הסנכרון של טבלה (03 §2.1): תדירות, אופק, אות חם, סבב קר. מוצהר בעץ בצומת `import` (05) וכתוב ל-`import_plan.sync`.
 * `coldColumn` — עמודת החלון לסבב (window): key של שדה תאריך מיובא; ברירת מחדל hot.column כשהוא תאריך מסמך.
 */
export const SyncSpec = z
  .object({
    every: z.number().int().positive(),
    horizon: z.number().int().positive().nullable(),
    hot: HotSpec,
    cold: ColdSpec,
    coldColumn: z.string().optional(),
  })
  .superRefine((s, ctx) => {
    if ((s.hot.signal === 'update_column' || s.hot.signal === 'document_date') && !s.hot.column)
      ctx.addIssue({ code: 'custom', message: `hot.signal ${s.hot.signal} requires hot.column`, path: ['hot', 'column'] });
    if (s.cold.by === 'window' && !s.coldColumn && !(s.hot.signal === 'document_date' && s.hot.column))
      ctx.addIssue({ code: 'custom', message: 'cold.by window requires coldColumn (or hot: document_date with column)', path: ['coldColumn'] });
  });
export type SyncSpec = z.infer<typeof SyncSpec>;

/** עמודת החלון של הסבב (window): coldColumn, אחרת עמודת תאריך המסמך של החם. */
export function coldColumnOf(s: Pick<SyncSpec, 'hot' | 'coldColumn'>): string | null {
  return s.coldColumn ?? (s.hot.signal === 'document_date' ? (s.hot.column ?? null) : null);
}

export const DEFAULT_SYNC_EVERY = 15;
export const WINDOW_BUCKET_DAYS = 30;
export const KEY_BUCKET_ROWS = 5_000;
/** מתחת לזה — `cold: all` (הטבלה כולה בכל מחזור); מעל — דליים. */
export const ALL_MAX_ROWS = 20_000;
export const UPDATE_COLUMN_DAYS = 7;
export const DOCUMENT_DATE_DAYS = 60;

/**
 * ברירות המחדל של 03 §2.1 לפי מה שיש בטבלה (הכרעת המשתמש 2026-10-11):
 * engine_log פעיל → engine_log + window/30/7 · עמודת עדכון → update_column/7d + window/30/1 · תאריך מסמך → document_date/60d + window/30/1 ·
 * engine_marker (אורקל, ORA_ROWSCN) → engine_marker + window/30/1 (עיוור למחיקות) · בלי תאריך: ≤ 20k → all · גדולה עם מפתח מספרי → key/5000/1.
 */
export function defaultSyncSpec(o: {
  engineLog?: boolean; engineMarker?: boolean; updateColumn?: string | null; dateColumn?: string | null; rows?: number | null; keyNumeric?: boolean;
  horizon?: number | null; every?: number;
}): SyncSpec {
  const every = o.every ?? DEFAULT_SYNC_EVERY;
  const horizon = o.horizon ?? null;
  const cold = (rotationDays: number): ColdSpec | null => (o.dateColumn ? { by: 'window', bucket: WINDOW_BUCKET_DAYS, rotationDays } : null);
  const noDate = (): ColdSpec => ((o.rows ?? 0) <= ALL_MAX_ROWS || !o.keyNumeric ? { by: 'all', bucket: 1, rotationDays: 1 } : { by: 'key', bucket: KEY_BUCKET_ROWS, rotationDays: 1 });
  const withCold = (hot: HotSpec, rotationDays: number): SyncSpec => ({ every, horizon: o.dateColumn ? horizon : null, hot, cold: cold(rotationDays) ?? noDate(), ...(o.dateColumn ? { coldColumn: o.dateColumn } : {}) });
  if (o.engineLog) return withCold({ signal: 'engine_log' }, 7);
  if (o.engineMarker) return withCold({ signal: 'engine_marker' }, 1);
  if (o.updateColumn) return withCold({ signal: 'update_column', column: o.updateColumn, days: UPDATE_COLUMN_DAYS }, 1);
  if (o.dateColumn) return withCold({ signal: 'document_date', column: o.dateColumn, days: DOCUMENT_DATE_DAYS }, 1);
  return withCold({ signal: 'none' }, 1);
}

/**
 * הצורה הישנה של ההגדרה (strategy/window/horizonMonths/everyMinutes, לפני 03 v2) → SyncSpec, לפי 03 §7:
 * digest/cursor עם חלון ⇒ document_date 60d + window/30/1 (אורקל: engine_marker — בלי DBMS_FLASHBACK המנוע נופל ל-document_date) ·
 * full ≤ 20k ⇒ all · full גדול ⇒ key/5000/1. `column` של החלון הישן היה key של שדה (בעץ) או שם עמודת מקור (ב-import_plan) —
 * הקורא מעביר את המיפוי. מיפוי זהה למיגרציה 0014 (sys.legacy_sync_spec).
 */
export function syncSpecFromLegacy(
  old: { strategy?: string; window?: { column: string } | null; horizonMonths?: number | null; everyMinutes?: number | null },
  o: { rows?: number | null; keyNumeric?: boolean; columnKey?: (c: string) => string; driver?: 'mssql' | 'oracle' } = {},
): SyncSpec {
  const col = old.window?.column ? (o.columnKey ? o.columnKey(old.window.column) : old.window.column.toLowerCase()) : null;
  return defaultSyncSpec({ dateColumn: col, engineMarker: o.driver === 'oracle' && !!col, rows: o.rows ?? null, keyNumeric: o.keyNumeric ?? true, horizon: old.horizonMonths ?? null, every: old.everyMinutes ?? DEFAULT_SYNC_EVERY });
}

/** binding של צומת import — חדש (sync) או ישן (strategy/window) → תמיד עם sync. */
export function normalizeImportSync<T extends { sync?: SyncSpec; strategy?: string; window?: { column: string } | null; horizonMonths?: number | null; everyMinutes?: number | null }>(b: T, o: { rows?: number | null; keyNumeric?: boolean } = {}): T & { sync: SyncSpec } {
  if (b.sync) return b as T & { sync: SyncSpec };
  const { strategy: _s, window: _w, horizonMonths: _h, everyMinutes: _e, ...rest } = b;
  return { ...(rest as T), sync: syncSpecFromLegacy(b, o) };
}

export const ImportTable = z
  .object({
    entity: EtlName,
    sourceTable: z.string().min(1),
    columns: z.array(ImportColumn).min(1),
    key: z.array(EtlName).min(1),
    sync: SyncSpec,
    filter: ImportFilter.optional(),
    /** מה-binding של הישות: טבלת מערכת (טבלת מערכת בסכימת המערכת) — סכימה משלה ובלי קידומת החברה (באג 9d457266). */
    sourceSchema: z.string().min(1).optional(),
    companyScoped: z.boolean().optional(),
  })
  .superRefine((t, ctx) => {
    const names = new Set(t.columns.filter((c) => !c.ignore).map((c) => c.name));
    for (const k of t.key) if (!names.has(k)) ctx.addIssue({ code: 'custom', message: `key column "${k}" is not an imported column`, path: ['key'] });
    const hc = t.sync.hot.column;
    if (hc && !names.has(hc)) ctx.addIssue({ code: 'custom', message: `sync.hot.column "${hc}" is not an imported column`, path: ['sync', 'hot', 'column'] });
    const cc = coldColumnOf(t.sync);
    if (t.sync.cold.by === 'window' && (!cc || !names.has(cc))) ctx.addIssue({ code: 'custom', message: `sync.cold window column "${cc ?? ''}" is not an imported column`, path: ['sync', 'coldColumn'] });
    if (names.has('company') || names.has('_loaded_at')) ctx.addIssue({ code: 'custom', message: 'company/_loaded_at are added automatically', path: ['columns'] });
  });
export type ImportTable = z.infer<typeof ImportTable>;

/**
 * הכתובת לטבלה אחת: sourceSchema של הישות גובר על סכימת החיבור; companyScoped:false ⇒ בלי קידומת החברה
 * (באורקל הקידומת היא החברה; טבלת מערכת יושבת בלעדיה). ברירת מחדל: כתובת החיבור כמו שהיא.
 */
export function tableAddress<A extends { schema: string | null; tablePrefix: string }>(base: A, t: { sourceSchema?: string | undefined; companyScoped?: boolean | undefined }): A {
  return { ...base, schema: t.sourceSchema ?? base.schema, tablePrefix: t.companyScoped === false ? '' : base.tablePrefix };
}

export const ImportPlan = z.object({
  worldId: Id,
  treeVersionId: Id,
  companies: z.array(z.string()),
  tables: z.array(ImportTable),
});
export type ImportPlan = z.infer<typeof ImportPlan>;

/** sys.import_plan — שורה לישות (רק לגרסה המפורסמת). */
export const ImportPlanRow = z.object({
  ...rowBase,
  worldId: Id,
  treeVersionId: Id,
  entity: EtlName,
  sourceTable: z.string(),
  columns: z.array(ImportColumn),
  key: z.array(z.string()),
  sync: SyncSpec,
  filter: ImportFilter.nullable(),
  companies: z.array(z.string()),
  enabled: z.boolean(),
});
export type ImportPlanRow = z.infer<typeof ImportPlanRow>;

/** מה הוכיח את המצב (03 §2.2): full_start = טעינה ראשונה · engine_log = לוג המנוע · window/key = דלי של הסבב. */
export const ProvenBy = z.enum(['full_start', 'engine_log', 'window', 'key']);
export type ProvenBy = z.infer<typeof ProvenBy>;

/** sys.sync_state — לכל (עולם, ישות, חברה). */
export const SyncState = z.object({
  ...rowBase,
  worldId: Id,
  entity: z.string(),
  company: z.string(),
  /** האות החם שרץ בפועל (אחרי נפילה — האות שאליו נפלו); null לפני המחזור הראשון. */
  signal: SignalKind.nullable(),
  state: Json,
  fingerprint: z.string(),
  rows: z.number().int().nonnegative(),
  asOf: Ts.nullable(),
  provenBy: ProvenBy.nullable(),
  degraded: z.boolean(),
  degradedReason: z.string().nullable(),
  horizonMonths: z.number().int().nullable(),
  lastOkAt: Ts.nullable(),
  lastAttemptAt: Ts.nullable(),
  lastError: z.string().nullable(),
});
export type SyncState = z.infer<typeof SyncState>;

/**
 * sys.direction_evidence (data-priority-field-type-direction): ראיית כיוון לעמודת טקסט (column) או סיכום לטיפוס מילון (column = null).
 * ספירות בלבד, בלי ערכים. `action`: fixed = הכריכה תוקנה בסריקה; proposed = חלוקה, הוגשה כהצעה; kept = כבר כך; none = בלי שינוי.
 */
export const DirectionEvidence = z.object({
  ...rowBase,
  worldId: Id,
  scanId: Id,
  table: z.string(),
  column: z.string().nullable(),
  sourceType: z.string(),
  prior: z.enum(['visual', 'logical']).nullable(),
  priorFrom: z.enum(['instance', 'template']).nullable(),
  measured: z.enum(['visual', 'logical', 'unknown']).nullable(),
  samples: z.number().int().nonnegative(),
  finalAtHead: z.number().int().nonnegative(),
  finalAtTail: z.number().int().nonnegative(),
  reversalEvidence: Json.nullable(),
  counter: z.number().int().nonnegative(),
  verdict: z.enum(['declared', 'suspected', 'logical', 'unknown', 'type_visual', 'type_logical', 'type_undecided']),
  action: z.enum(['fixed', 'kept', 'proposed', 'none']).nullable(),
  scannedAt: Ts,
  templateVersion: z.string().nullable(),
});
export type DirectionEvidence = z.infer<typeof DirectionEvidence>;

export const SyncTrigger = z.enum(['timer', 'manual', 'refresh', 'after_publish']);
export type SyncTrigger = z.infer<typeof SyncTrigger>;

export const SyncRunStatus = z.enum(['running', 'ok', 'partial', 'failed']);
export type SyncRunStatus = z.infer<typeof SyncRunStatus>;

/** sys.sync_run */
export const SyncRun = z.object({
  ...rowBase,
  worldId: Id,
  trigger: SyncTrigger,
  status: SyncRunStatus,
  startedAt: Ts,
  finishedAt: Ts.nullable(),
  tables: Json,
  jobIds: z.array(Id),
  error: z.string().nullable(),
});
export type SyncRun = z.infer<typeof SyncRun>;

/** sync.failed / sync.stale — הטבלה degraded (06 asOf); sync.fallback / sync.needs_attention — 03 §5 (נפילת אות, מפסק); connector.disconnected — 02; api.restarted — ה-api הופעל מחדש אחרי קריסה (12 §1), subject 'api'. */
export const AlertKind = z.enum(['sync.failed', 'sync.stale', 'sync.fallback', 'sync.needs_attention', 'connector.disconnected', 'api.restarted', 'artifact_db.over_limit', 'trigger.failed', 'trigger.paused', 'mail.reauth_required', 'mail.send_failed']);
export type AlertKind = z.infer<typeof AlertKind>;

/** sys.api_run (12 §1): שורה לכל עליית api; heartbeat ב-lastSeenAt; endedAt/clean בסגירה מסודרת — שורה שלא נסגרה בנקי = קריסה. */
export const ApiRun = z.object({
  ...rowBase,
  startedAt: Ts,
  lastSeenAt: Ts,
  endedAt: Ts.nullable(),
  clean: z.boolean().nullable(),
  version: z.string().nullable(),
  hostname: z.string().nullable(),
  heapLimitMb: z.number().int().nullable(),
});
export type ApiRun = z.infer<typeof ApiRun>;

/** שורת שינוי שנקלט (03 §6, sys.sync_change, append-only; שמירה 30 יום): זרם האירועים של ה-ERP. */
export const SyncChangeOp = z.enum(['I', 'U', 'D']);
export type SyncChangeOp = z.infer<typeof SyncChangeOp>;
export const SyncChange = z.object({
  id: Id,
  instanceId: Id,
  worldId: Id,
  entity: z.string(),
  company: z.string(),
  key: Json,
  op: SyncChangeOp,
  sourceVersion: z.string().nullable(),
  cycleId: Id.nullable(),
  ts: Ts,
});
export type SyncChange = z.infer<typeof SyncChange>;

/** sys.sync_state.state (03 §2.2, state_version 3.0.0). */
export const SYNC_STATE_VERSION = '3.0.0';
export type SyncProgress = { nextBucket: string | null; done: number; total: number; attempts?: number; version?: string | null; startedAt?: string | null };
export type SyncStateV3 = {
  state_version: typeof SYNC_STATE_VERSION;
  fingerprint: string;
  /** true אחרי טעינה ראשונה; אחרת ההתקדמות (דלי הבא, כמה נטענו) */
  loaded: true | { progress: SyncProgress };
  hot:
    | { signal: 'engine_log'; version: string | null }
    | {
        signal: 'engine_marker'; version: string | null;
        /**
         * הסמן מדווח ביתר (change data-sync-marker-over-report): במחזור האחרון הסמן סימן יותר מפתחות מהסף (03 §3.2) — החם דולג,
         * `version` התקדם ל-SCN הנוכחי, והסבב הקר (יום) מכסה. `keys` = כמה סומנו, `loaded` = שורות בטבלה, `cycles` = מחזורים רצופים. נמחק כשהסמן חוזר לדווח סביר.
         */
        overReported?: { keys: number; loaded: number; at: string; since: string; cycles: number };
      }
    | { signal: 'update_column' | 'document_date'; lastAt: string | null }
    | { signal: 'none' };
  cold: {
    nextBucket: string | null; verifiedAt: string | null; cycleStartedAt: string | null;
    /** כמה דליים באופק (תצוגה) · מונה מחזורים (קצב הסבב) · רשת מפתח של הסבב הנוכחי (key) · דלי ה-MIN (window בלי אופק) */
    buckets?: number; cycles?: number; grid?: { lo: number; size: number; count: number }; minBucket?: string;
    /**
     * אימות ממתין: דלי הסבב נכשל במקור (timeout וכו'). החם כבר נכתב ו-as_of התקדם; nextBucket לא זז והדלי ינוסה שוב
     * בתורו. נפרד מהמפסק — כשל אימות לא חוסם את העדכון השוטף. נמחק כשדלי נטען בהצלחה.
     */
    pending?: { bucket: string; since: string; failures: number; reason: string };
  };
  fallback: null | { from: SignalKind; to: SignalKind; since: string; reasonHe: string };
  /** המפסק (03 §3.5): כשלים רצופים ומתי מותר לנסות שוב */
  breaker?: { failures: number; until: string | null };
};

/** sys.alert */
export const Alert = z.object({
  ...rowBase,
  kind: AlertKind,
  subject: z.string(),
  messageHe: z.string(),
  resolvedAt: Ts.nullable(),
});
export type Alert = z.infer<typeof Alert>;
